import {
  QueryBuilderClass,
  QueryBuilderModel,
  QueryBuilderProperty,
} from 'vet-bones/utils/queryBuilder';

import {
  Model,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptClass,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { getClassHierarchy } from 'src/containers/ProjectDiagram/utils/getClassHierarchy';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';

export const getQueryBuilderModel = (
  model: Model | ModelSnapshot
): QueryBuilderModel => {
  const {
    linkDataArray,
    nodeDataArray,
    modelData: { attributesByIri, colorIdsByIri, labelsByIri, draftData },
  } = model;

  const outgoingProperties: Record<string, Set<string>> = {};
  const incomingProperties: Record<string, Set<string>> = {};
  const classesByIri: Record<string, QueryBuilderClass> = {};
  const propertiesByIri: Record<string, QueryBuilderProperty> = {};

  const addClassIriToProperties =
    (property: string, properties: Record<string, Set<string>>) =>
    (classIri: string) => {
      if (!properties[classIri]) {
        // eslint-disable-next-line no-param-reassign
        properties[classIri] = new Set();
      }
      properties[classIri].add(property);
    };

  // add attributes
  Object.values(attributesByIri).forEach((data) => {
    const domains = Object.keys(data.domains);
    domains.forEach(addClassIriToProperties(data.value, outgoingProperties));
    const ranges = Object.keys(data.ranges);
    ranges.forEach(addClassIriToProperties(data.value, incomingProperties));
    propertiesByIri[data.value] = {
      iri: data.value,
      label: getLabelForIri(labelsByIri, data.value),
      isDatatype: true,
      domains: new Set(domains),
      ranges: new Set(ranges),
    };
  });

  // add relationships
  linkDataArray.filter(isModelConceptRelationship).forEach((data) => {
    if (!propertiesByIri[data.value]) {
      propertiesByIri[data.value] = {
        iri: data.value,
        label: getLabelForIri(labelsByIri, data.value),
        isDatatype: false,
        domains: new Set(),
        ranges: new Set(),
      };
    }

    if (data.from) {
      const fromIri = getModelConceptClassValue(data.from);
      addClassIriToProperties(data.value, outgoingProperties)(fromIri);
      propertiesByIri[data.value].domains.add(fromIri);
    }

    if (data.to) {
      const toIri = getModelConceptClassValue(data.to);
      addClassIriToProperties(data.value, incomingProperties)(toIri);
      propertiesByIri[data.value].ranges.add(toIri);
    }
  });

  // add classes
  nodeDataArray.filter(isModelConceptClass).forEach((data) => {
    classesByIri[data.value] = {
      iri: data.value,
      label: getLabelForIri(labelsByIri, data.value),
      color: '',
      colorId: (!draftData && colorIdsByIri[data.value]) || 'sd-qb-black',
      designerColorId: '',
      ancestors: new Set(),
      descendants: new Set(),
      incomingProperties: new Set(incomingProperties[data.value]),
      outgoingProperties: new Set(outgoingProperties[data.value]),
    };
  });

  const hierarchyByIri = getClassHierarchy(
    linkDataArray,
    new Set(Object.keys(classesByIri))
  );

  Object.keys(classesByIri).forEach((iri) => {
    const { ancestors, descendants } = hierarchyByIri[iri];
    classesByIri[iri].ancestors = new Set(Object.keys(ancestors));
    classesByIri[iri].descendants = new Set(Object.keys(descendants));

    // all ancestor properties also apply to this class
    Object.keys(ancestors).forEach((ancestor) => {
      incomingProperties[ancestor]?.forEach((propertyIri) => {
        classesByIri[iri].incomingProperties.add(propertyIri);
        propertiesByIri[propertyIri].ranges.add(iri);
      });
      outgoingProperties[ancestor]?.forEach((propertyIri) => {
        classesByIri[iri].outgoingProperties.add(propertyIri);
        propertiesByIri[propertyIri].domains.add(iri);
      });
    });
  });

  return { classesByIri, propertiesByIri };
};
